class AeyaBlogGrid extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.blogUrl = this.dataset.blogUrl;
    this.toggle = this.querySelector('[data-filters-toggle]');
    this.layout = this.querySelector('[data-layout]');

    this.onToggle = this.onToggle.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
    this.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  filtersOpen() {
    return !!this.layout && this.layout.hasAttribute('data-filters-open');
  }

  onToggle() {
    this.filtersOpen() ? this.closeFilters() : this.openFilters();
  }

  openFilters() {
    if (!this.layout) return;
    this.layout.setAttribute('data-filters-open', '');
    this.setToggleState(true);
  }

  closeFilters() {
    if (!this.layout) return;
    this.layout.removeAttribute('data-filters-open');
    this.setToggleState(false);
  }

  setToggleState(open) {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    const labelOpen = this.toggle.querySelector('[data-filters-label-open]');
    const labelClose = this.toggle.querySelector('[data-filters-label-close]');
    if (labelOpen) labelOpen.hidden = open;
    if (labelClose) labelClose.hidden = !open;
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.filtersOpen()) this.closeFilters();
  }

  onDocClick(event) {
    const chip = event.target.closest('[data-filter-chip]');
    if (chip) {
      event.preventDefault();
      this.applyFilter(chip.getAttribute('href'));
      return;
    }
    const loadMore = event.target.closest('[data-loadmore-link]');
    if (loadMore) {
      event.preventDefault();
      this.loadMore(loadMore.getAttribute('href'));
    }
  }

  sectionUrl(url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.toString();
  }

  async fetchSection(url) {
    const response = await fetch(this.sectionUrl(url));
    if (!response.ok) throw new Error('Fetch failed');
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async applyFilter(url) {
    const results = this.querySelector('[data-results]');
    if (!results) {
      window.location.assign(url);
      return;
    }
    results.style.opacity = '0.5';
    results.style.pointerEvents = 'none';
    try {
      const doc = await this.fetchSection(url);
      const newResults = doc.querySelector('[data-results]');
      if (newResults) results.innerHTML = newResults.innerHTML;

      this.querySelectorAll('[data-filter-chip]').forEach((chip) => {
        const isActive = chip.getAttribute('href') === url;
        chip.classList.toggle('is-active', isActive);
        if (isActive) {
          chip.setAttribute('aria-current', 'true');
        } else {
          chip.removeAttribute('aria-current');
        }
      });

      history.pushState({}, '', url);
      this.querySelector('.aeya-blog-grid__head')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      window.location.assign(url);
    } finally {
      results.style.opacity = '';
      results.style.pointerEvents = '';
    }
  }

  async loadMore(url) {
    const grid = this.querySelector('[data-grid]');
    const loadmore = this.querySelector('[data-loadmore]');
    if (!grid) return;
    if (loadmore) loadmore.classList.add('is-loading');
    try {
      const doc = await this.fetchSection(url);
      const newItems = doc.querySelectorAll('[data-grid] > .aeya-blog-grid__item');
      newItems.forEach((item) => grid.appendChild(item));

      const nextLoadmore = doc.querySelector('[data-loadmore-link]');
      if (nextLoadmore && loadmore) {
        const link = loadmore.querySelector('[data-loadmore-link]');
        if (link) link.setAttribute('href', nextLoadmore.getAttribute('href'));
        loadmore.classList.remove('is-loading');
      } else if (loadmore) {
        loadmore.remove();
      }
    } catch (e) {
      window.location.assign(url);
    }
  }
}

if (!customElements.get('aeya-blog-grid')) {
  customElements.define('aeya-blog-grid', AeyaBlogGrid);
}
